'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Badge } from 'primereact/badge';
import { Skeleton } from 'primereact/skeleton';
import { getCartCount, initServerCart, CART_EVENT } from '@/lib/cart';

const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

export const CartButton = ({ loading = false }) => {
    const [count,   setCount]   = useState(0);
    const [hovered, setHovered] = useState(false);
    const [shaking, setShaking] = useState(false);
    const pathname = usePathname();
    const active = pathname === '/cart';

    useEffect(() => {
        if (getAuthUser()) initServerCart();

        const update = () => setCount(getCartCount());
        update();

        const onShake = () => {
            setShaking(true);
            setTimeout(() => setShaking(false), 650);
        };

        window.addEventListener(CART_EVENT, update);
        window.addEventListener('storage', update);
        window.addEventListener('ennix:cart-shake', onShake);
        return () => {
            window.removeEventListener(CART_EVENT, update);
            window.removeEventListener('storage', update);
            window.removeEventListener('ennix:cart-shake', onShake);
        };
    }, []);

    if (loading) {
        return (
            <div className="flex flex-column align-items-center gap-1 px-2 md:px-3 py-2">
                <Skeleton shape="circle" size="1.6rem" />
                <Skeleton width="1.8rem" height="0.5rem" borderRadius="4px" />
            </div>
        );
    }

    const color = active || hovered ? 'var(--primary-color)' : '#6b7280';

    return (
        <>
            <style>{`
                @keyframes cart-btn-shake {
                    0%,100% { transform: scale(1)    rotate(0deg);   }
                    15%     { transform: scale(1.35) rotate(-18deg); }
                    30%     { transform: scale(1.2)  rotate(14deg);  }
                    45%     { transform: scale(1.25) rotate(-10deg); }
                    60%     { transform: scale(1.1)  rotate(7deg);   }
                    75%     { transform: scale(1.05) rotate(-4deg);  }
                }
            `}</style>
            <Link
                href="/cart"
                className="no-underline flex gap-1 flex-column align-items-center justify-content-between gap-1 px-2 md:px-3 py-2 border-round-lg cursor-pointer transition-all transition-duration-200"
                style={{
                    minWidth: '3.5rem',
                    background: hovered ? 'var(--primary-50)' : 'transparent',
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <i
                    className="pi pi-shopping-cart p-overlay-badge"
                    style={{
                        fontSize: '1.35rem',
                        color,
                        height: '1.75rem',
                        transition: 'color 0.2s',
                        ...(shaking ? { animation: 'cart-btn-shake 0.55s cubic-bezier(0.36,0.07,0.19,0.97) both' } : {}),
                    }}
                >
                    {count > 0 && (
                        <Badge value={count > 99 ? '99+' : String(count)} severity="danger" />
                    )}
                </i>
                <span style={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color,
                    transition: 'color 0.2s',
                    lineHeight: 1,
                }}>
                    Cart
                </span>
            </Link>
        </>
    );
};
